import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";

const verdictConfig = {
  supported: { icon: ShieldCheck, color: "text-green-700 bg-green-50 border-green-200", label: "Supported" },
  partially_supported: { icon: ShieldQuestion, color: "text-yellow-700 bg-yellow-50 border-yellow-200", label: "Partial" },
  unsupported: { icon: ShieldAlert, color: "text-red-700 bg-red-50 border-red-200", label: "Unsupported" },
};

export default function FaithfulnessPanel({ faithfulness }) {
  if (!faithfulness || !faithfulness.claims || faithfulness.claims.length === 0) return null;

  const supported = faithfulness.claims.filter(c => c.verdict === "supported").length;
  const unsupported = faithfulness.claims.filter(c => c.verdict === "unsupported").length;
  const partial = faithfulness.claims.filter(c => c.verdict === "partially_supported").length;
  const total = faithfulness.claims.length;
  const score = faithfulness.faithfulness_score ?? 0;

  return (
    <Card className="border border-border shadow-none" data-testid="faithfulness-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-muted-foreground" />
            Faithfulness Audit
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">
              {score}/100
            </Badge>
          </div>
        </div>
        <div className="flex gap-3 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            {supported} supported
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            {partial} partial
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            {unsupported} unsupported
          </span>
          <span className="text-muted-foreground">/ {total} claims</span>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[350px] pr-2">
          <div className="space-y-2">
            {faithfulness.claims.map((claim, idx) => {
              const cfg = verdictConfig[claim.verdict] || verdictConfig.supported;
              const Icon = cfg.icon;
              return (
                <div
                  key={idx}
                  className={`border rounded-sm p-3 ${cfg.color}`}
                  data-testid={`claim-${idx}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">"{claim.claim}"</p>
                      {claim.reasoning && (
                        <p className="text-xs mt-1 opacity-75">{claim.reasoning}</p>
                      )}
                      {claim.supporting_chunks?.length > 0 && (
                        <div className="flex gap-1 mt-1.5">
                          {claim.supporting_chunks.map(ci => (
                            <Badge key={ci} variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-mono">
                              Chunk {ci}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] font-mono flex-shrink-0">
                      {cfg.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
